import { Icon, type IconName } from "../icons/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip/tooltip";
import { cn } from "../../lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { Slot } from "radix-ui";

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow,opacity] outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50 data-[focus]:ring-[3px] data-[focus]:ring-ring data-[loading]:cursor-wait data-[pressed]:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 data-[hover]:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 data-[hover]:bg-destructive/90 data-[focus]:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:data-[focus]:ring-destructive/40",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground data-[hover]:bg-accent data-[hover]:text-accent-foreground",
        ghost:
          "hover:bg-accent hover:text-accent-foreground data-[hover]:bg-accent data-[hover]:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 data-[hover]:bg-secondary/80",
        link: "px-0! text-foreground underline-offset-4 hover:underline data-[hover]:underline",
      },
      size: {
        default: "h-8 px-3 py-2",
        sm: "h-7 px-2 py-2",
        xs: "h-6 p-2",
        lg: "h-10 px-6 py-2",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-xs": "size-6 [&_svg]:size-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  disabledReason?: React.ReactNode;
  loading?: boolean;
  prefixIcon?: IconName;
  prefixIconClassName?: string;
  suffixIcon?: IconName;
}

function Button({
  asChild = false,
  children,
  className,
  disabled,
  disabledReason,
  loading = false,
  onClickCapture,
  prefixIcon,
  prefixIconClassName,
  ref,
  size,
  suffixIcon,
  type = "button",
  variant,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const shouldShowDisabledReason = disabled && disabledReason;
  const Comp = asChild ? Slot.Root : "button";
  const commonProps = {
    "aria-busy": loading || undefined,
    "data-loading": loading || undefined,
    "data-slot": "button",
    className: cn(buttonVariants({ variant, size, className })),
  };

  const prefix = loading ? (
    <Icon src="LoaderCircle" className="animate-spin" />
  ) : (
    prefixIcon && <Icon src={prefixIcon} className={prefixIconClassName} />
  );

  const button = asChild ? (
    <Comp
      {...commonProps}
      ref={ref}
      aria-disabled={isDisabled || undefined}
      onClickCapture={(event) => {
        onClickCapture?.(event);

        if (isDisabled) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      {...props}
    >
      {prefix}
      <Slot.Slottable>{children}</Slot.Slottable>
      {suffixIcon && <Icon src={suffixIcon} />}
    </Comp>
  ) : (
    <Comp
      {...commonProps}
      ref={ref}
      disabled={isDisabled}
      type={type}
      onClickCapture={onClickCapture}
      {...props}
    >
      {prefix}
      <Slot.Slottable>{children}</Slot.Slottable>
      {suffixIcon && <Icon src={suffixIcon} />}
    </Comp>
  );

  if (shouldShowDisabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-slot="button-disabled-reason-wrapper"
            className="inline-flex"
          >
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    );
  }

  return button;
}

export { Button, buttonVariants };
